import type { EGenderType } from './person.interface';
import type { TRegistrationPersonUpdateChoice } from './registration-person.model';

/**
 * The seven questions of the health questionnaire (PAR-Q), named as the
 * physical-assessment anamnesis stores them: that is where the counter takes
 * the answers, so the names are the same and nothing is translated on the way.
 */
export type TRegistrationParqQuestion =
  | 'ParQ1'
  | 'ParQ2'
  | 'ParQ3'
  | 'ParQ4'
  | 'ParQ5'
  | 'ParQ6'
  | 'ParQ7';

/** The questions, in the order they are asked. */
export const REGISTRATION_PARQ_QUESTIONS: readonly TRegistrationParqQuestion[] = [
  'ParQ1',
  'ParQ2',
  'ParQ3',
  'ParQ4',
  'ParQ5',
  'ParQ6',
  'ParQ7',
];

/** What the person answered to the health questionnaire on the public link. */
export interface IRegistrationParqAnswers {
  /**
   * Which set of questions the answers are to, stamped by the Worker. A later
   * change of a question gets a new number, so an older answer is never read
   * as an answer to the new text.
   */
  version: number;
  /** `true` is "yes". The Worker takes all seven answers or none. */
  answers: Record<TRegistrationParqQuestion, boolean>;
  /**
   * The person's authorisation for the gym to use the answers, given on the
   * first health screen of the public form. Always present: the Worker keeps
   * no answers without it, and hands none out.
   */
  consent: {
    /** Which consent text was on screen, stamped by the Worker. */
    version: number;
    /**
     * When the Worker took the consent. An instant in UTC on the wire; the
     * candidate request declares it, so here it is already in the account's
     * time.
     */
    acceptedAt: string;
  };
}

/** The registration link a candidate came through. */
export interface IRegistrationCandidateLink {
  id: string;
  slug: string;
  /** The staff's label. `''` while nobody named the link. */
  name: string;
}

/**
 * Where a registration of a link is: waiting at the counter, handled, or in
 * the trash.
 */
export type TRegistrationCandidateStatus = 'pending' | 'imported' | 'trashed';

/**
 * Candidata a novo cadastro, gravada pelo formulário público no Worker de
 * pré-cadastro (`registrationworker-api`) e à espera de conferência ao balcão.
 *
 * camelCase, e é o contrato correto: este endpoint é do nosso Worker, não do
 * backend Actuar. A regra de PascalCase vale para os `*ViewModel` do C#.
 */
export interface IRegistrationCandidate {
  id: string;
  /** Só dígitos, 11 caracteres. */
  fiscalIdentity: string;
  firstName: string;
  lastName: string;
  /** ISO, AAAA-MM-DD. */
  birthdate: string;
  /** Mesmos códigos do `GenderType` do backend. */
  genderType?: EGenderType;
  email: string;
  /** Só dígitos, sem DDI. */
  phoneNumber: string;
  phoneCountryCode: number;
  address: {
    zipCode: string;
    street: string;
    /** `S/N` when the person said the address has no number. */
    number: string;
    /**
     * Block and lot, building, apartment. Absent on a cadastro sent before the
     * link asked for it: the worker of that time did not keep the field.
     */
    complement?: string;
    district: string;
    city: string;
    regionName: string;
  };
  emergencyContact: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    phoneCountryCode: number;
  };
  /**
   * The link the registration came through. Absent on a Worker older than the
   * list of links, where a gym has only one.
   */
  link?: IRegistrationCandidateLink;
  /**
   * The health questionnaire. Absent when it was not asked: a link with the
   * questionnaire off, a cadastro sent before it existed, or a Worker older
   * than it. Absent is "not asked", never "every answer was no".
   */
  parq?: IRegistrationParqAnswers;
  /**
   * When the Worker took the registration. An instant in UTC on the wire; the
   * candidate request declares it, so here it is already in the account's time.
   */
  createdAt: string;
}

/**
 * The comment that records the health answers on the person's profile. Fixed
 * texts and the answers only, never anything the person typed.
 *
 * PascalCase because it is the body of the personservice `POST /Comment`, which
 * also takes the `TargetPersonId` the request adds once the person exists.
 */
export interface IRegistrationHealthComment {
  /** At most 100 characters. */
  Title: string;
  /** HTML, at most 10 000 characters. */
  Content: string;
  /** The staff member who confirmed the registration. */
  SourcePersonId: string;
}

/**
 * What the health answers of a candidate still owe the person, decided and
 * frozen at the click that created them, plus how far the writes got.
 *
 * The three marks are what make a retry safe: it writes only what is missing.
 */
export interface IRegistrationHealthImport {
  comment: IRegistrationHealthComment;
  /**
   * The answers for the physical-assessment anamnesis, or `null` when they do
   * not go there: the gym has no such module, or the staff member may not
   * record an anamnesis.
   */
  anamnesis: Record<TRegistrationParqQuestion, boolean> | null;
  /** The comment is on the profile. */
  commentWritten?: boolean;
  /**
   * The anamnesis request went out. Set before the request and never cleared:
   * the row is keyed by the person, so a second POST would collide with the
   * first, whatever became of it.
   */
  anamnesisSent?: boolean;
  /** The one attempt at the anamnesis failed: the answers are in the comment only. */
  anamnesisFailed?: boolean;
}

/**
 * What of a registration is written on a person: the part of the candidate the
 * person update reads, and nothing of the health answers.
 */
export type TRegistrationLinkData = Pick<
  IRegistrationCandidate,
  | 'firstName'
  | 'lastName'
  | 'birthdate'
  | 'genderType'
  | 'email'
  | 'phoneNumber'
  | 'phoneCountryCode'
  | 'address'
>;

/**
 * What a registration sent by someone who ALREADY is a person of the gym owes
 * that person, frozen at the click, plus how far the update got.
 *
 * The inputs are kept, never the body: each attempt reads the person again and
 * builds the body from what is there at that moment. A body kept from the
 * first attempt would carry its phone and address without an id, and sending
 * it twice would insert both twice.
 */
export interface IRegistrationAttachImport {
  choice: TRegistrationPersonUpdateChoice;
  link: TRegistrationLinkData;
  /**
   * The link's e-mail may be written on a person who has none: the answer of
   * the check made before the click. The check is asked again before each
   * write that carries the e-mail, and it turns `false` for good once that
   * check, or the person service, refuses it.
   */
  emailAllowed: boolean;
  /** The update is on the person, or there was nothing to write. */
  updateWritten?: boolean;
  /**
   * The check at the counter had let the link's e-mail in and it was refused
   * at the write, by the check asked again or by the person service: the rest
   * was written without it.
   */
  emailRefused?: boolean;
}

/**
 * Pessoa já criada no personservice cuja candidata ainda não ficou marcada como
 * importada no Worker. É o que a nova tentativa reusa para chamar SÓ o import.
 */
export interface IRegistrationCandidateImport {
  candidateId: string;
  personId: string;
  /**
   * Absent when the candidate answered no health questionnaire: the import is
   * then the single request it always was.
   */
  health?: IRegistrationHealthImport;
  /**
   * Present when the person already existed before the registration, which is
   * then attached to it: no person was created, and `personId` is that
   * person's. The health answers of such a registration go to the profile
   * comment only, never to the anamnesis.
   */
  attach?: IRegistrationAttachImport;
}

/** What the counter looks a registration up by. */
export interface IRegistrationCandidateQuery {
  fiscalIdentity: string;
  /** The link picked at the counter. Absent, the Worker looks in the default link. */
  linkId?: string;
}

/** What the counter asks for when it attaches a registration to an existing person. */
export interface IRegistrationAttachRequest {
  choice: TRegistrationPersonUpdateChoice;
  /**
   * The comment with the health answers, when the registration has them.
   * Written by the screen, as for a new person.
   */
  healthComment?: IRegistrationHealthComment;
}

/**
 * One line of the lists of a link. It never carries the health answers: a list
 * says who is there, and nobody's answers travel in bulk.
 */
export type IRegistrationCandidateListItem = Omit<IRegistrationCandidate, 'parq' | 'link'> & {
  link: IRegistrationCandidateLink;
  status: TRegistrationCandidateStatus;
  /** Only in the trash: when it was sent there. */
  trashedAt?: string;
  /** Only once handled: the person the counter tied it to. */
  personId?: string;
  /** Only once handled: when the counter marked it. */
  importedAt?: string;
};

/** What one of the lists of registrations is asked for. */
export interface IRegistrationCandidateListQuery {
  /** One link only. Absent, every link of the gym. */
  linkId?: string;
  status: TRegistrationCandidateStatus;
  /** The Worker takes at most 100. */
  limit: number;
  offset: number;
}

/** A page of a list, and how many the whole list has. */
export interface IRegistrationCandidateList {
  items: IRegistrationCandidateListItem[];
  total: number;
}

/** Days a registration stays in the trash. Mirrors TRASH_RETENTION_DAYS of the Worker. */
export const REGISTRATION_TRASH_RETENTION_DAYS = 15;
/** Days a registration nobody handled is kept, in the trash or not. Mirrors PENDING_RETENTION_DAYS. */
export const REGISTRATION_PENDING_RETENTION_DAYS = 90;
/** Rows a page of a list asks for. The Worker takes at most 100. */
export const REGISTRATION_CANDIDATE_LIST_PAGE_SIZE = 20;

/** Which page of which list of which link the screen shows. */
export interface IRegistrationCandidateListPage {
  linkId: string;
  status: TRegistrationCandidateStatus;
  /** 0-based. */
  page: number;
}
